import { Source } from "Constant/source";

import AdjustSource from "components/source_types/AdjustSource";
import AppsFlyerSource from "components/source_types/AppsFlyerSource";
import B24Source from "components/source_types/B24Source";
import BigquerySource from "components/source_types/BigquerySource";
import Dynamics365Source from "components/source_types/Dynamics365Source";
import FreshsalesSource from "components/source_types/FreshsalesSource";
import LeadsquaredSource from "components/source_types/LeadsquaredSource";
import MondaySource from "components/source_types/MondaySource";
import OdooSource from "components/source_types/OdooSource";
import PipedriveSource from "components/source_types/PipedriveSource";
import SalesForceSource from "components/source_types/SalesForceSource";
import VtigerSource from "components/source_types/VtigerSource";
import ZendeskSource from "components/source_types/ZendeskSource";

/** Offline message source; not part of the `Source` enum. */
export const MSG_SOURCE_TYPE = "msg";

export const SourceCreationView = Object.freeze({
    FORM: "FORM",
    MAPPING: "MAPPING",
});

export const WEB_SOURCE_TYPES = [Source.js, Source.redirect];

export const SFTP_SOURCE_TYPES = [Source.sftp, Source.ftp];

export const APP_CAPI_SOURCE_TYPES = [Source.androidcapi, Source.ioscapi];

/** Source types that render the multi-select "Usage" field. */
export const USAGE_SELECT_SOURCE_TYPES = [
    Source.sftp,
    Source.ftp,
    Source.capi,
    MSG_SOURCE_TYPE,
    Source.spreadsheet,
    Source.upload,
];

/**
 * CRM / MMP source types rendered via `source_types/*` components.
 * `blockerProp` is the prop name the child uses to report that it moved past
 * its first step (hides the source type select while it is truthy).
 * `useHostFooter` components hide their own buttons and register their
 * primary action with the modal footer instead (see `useHostFooterAction`).
 */
export const CRM_SOURCE_TYPES = Object.freeze({
    [Source.salesforce]: { Component: SalesForceSource, useHostFooter: true },
    [Source.freshsales]: { Component: FreshsalesSource, useHostFooter: true },
    [Source.vtiger]: { Component: VtigerSource, useHostFooter: true },
    [Source.odoo]: {
        Component: OdooSource,
        blockerProp: "setOdooStep",
        useHostFooter: true,
    },
    [Source.lsqrd]: { Component: LeadsquaredSource, useHostFooter: true },
    [Source.appsflyer]: {
        Component: AppsFlyerSource,
        blockerProp: "setAppsflyerDataStus",
        useHostFooter: true,
    },
    [Source.adjust]: {
        Component: AdjustSource,
        blockerProp: "setAdjustDataStus",
        useHostFooter: true,
    },
    [Source.dynamics]: {
        Component: Dynamics365Source,
        blockerProp: "setDynamicsStep",
        useHostFooter: true,
    },
    [Source.zendesk_sell]: { Component: ZendeskSource, useHostFooter: true },
    [Source.pipe]: { Component: PipedriveSource, useHostFooter: true },
    [Source.b24]: { Component: B24Source, useHostFooter: true },
    [Source.bigquery]: {
        Component: BigquerySource,
        blockerProp: "setBigqueryStep",
        useHostFooter: true,
    },
    [Source.monday]: { Component: MondaySource, useHostFooter: true },
});

export const CRM_SOURCE_TYPE_CODES = Object.keys(CRM_SOURCE_TYPES);

/** CRM / MMP codes whose primary action is rendered by the modal footer. */
export const HOST_FOOTER_CRM_CODES = CRM_SOURCE_TYPE_CODES.filter(
    (code) => CRM_SOURCE_TYPES[code].useHostFooter,
);

/**
 * Source types whose non-OAuth primary action (credential "Continue", mapping
 * Save, next step, create) is registered with the modal footer via
 * `useHostFooterAction`. OAuth "Connect with …" buttons always stay in the
 * form content; while they are the only action the footer shows Back/Cancel.
 */
export const FOOTER_ACTION_SOURCE_TYPES = [
    ...HOST_FOOTER_CRM_CODES,
    Source.zoho,
    Source.hub,
    Source.spreadsheet,
];

/** Source types that do not render the generic "Domain" field. */
export const DOMAIN_FIELD_EXCLUDED_SOURCE_TYPES = [
    ...CRM_SOURCE_TYPE_CODES,
    ...SFTP_SOURCE_TYPES,
    ...APP_CAPI_SOURCE_TYPES,
    Source.zoho,
    Source.hub,
    Source.spreadsheet,
    Source.upload,
];

export const DEFAULT_USAGE_LABEL_BY_SOURCE_TYPE = Object.freeze({
    [Source.js]: "Web",
    [Source.redirect]: "Web",
    [Source.androidcapi]: "App",
    [Source.ioscapi]: "App",
    [Source.capi]: "Offline",
    [MSG_SOURCE_TYPE]: "Offline",
    [Source.ftp]: "Offline",
    [Source.sftp]: "Offline",
    [Source.upload]: "Offline",
    [Source.spreadsheet]: "Offline",
});

export const CRM_USAGE_LABEL = "CRM";

export const GENERIC_HOST = "cpi.ssevt.com";

export const DEFAULT_HOST = "cpi";

export const SFTP_PORT = 22;

export const FTP_PORT = 21;

export const FTP_TRANSFER_MODES = ["default", "active", "passive"];

export const SFTP_READ_TYPES = Object.freeze({
    FILE: "file",
    DIRECTORY: "directory",
});

export const EMPTY_SFTP_CREDENTIALS = Object.freeze({
    domain: "",
    username: "",
    type: "",
    password: "",
});

export const EMPTY_LEAD_MAPPINGS = Object.freeze({
    leadgen_id: "",
    leadStatus: "",
    date: "",
});

export const ZOHO_LEAD_MAPPING_KEYS = Object.freeze({
    leadgen_id: "Lead_id",
    leadStatus: "Lead_Status",
    date: "Modified_Time",
});

export const HUBSPOT_USAGE_TYPE_IDS = [4];

export const SPREADSHEET_PAGE_SIZE_OPTIONS = [5, 10, 25];
